import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client-wrapper";
import { useAuth } from "@/hooks/useAuth";

type ThemeMode = "light" | "dark";
type ThemeColor = "blue" | "pink" | "green" | "purple" | "orange";

interface ThemeContextType {
  mode: ThemeMode;
  color: ThemeColor;
  setMode: (mode: ThemeMode) => void;
  setColor: (color: ThemeColor) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [mode, setModeState] = useState<ThemeMode>("light");
  const [color, setColorState] = useState<ThemeColor>("blue");

  useEffect(() => {
    if (user) {
      loadSettings();
    }
  }, [user]);

  const loadSettings = async () => {
    if (!user) return;

    const { data } = await supabase
      .from("user_settings")
      .select("*")
      .eq("user_id", user.id)
      .single();

    if (data) {
      setModeState(data.theme_mode as ThemeMode);
      setColorState(data.theme_color as ThemeColor);
      applyTheme(data.theme_mode as ThemeMode, data.theme_color as ThemeColor);
    }
  };

  const setMode = async (newMode: ThemeMode) => {
    setModeState(newMode);
    applyTheme(newMode, color);
    await saveSettings(newMode, color);
  };

  const setColor = async (newColor: ThemeColor) => {
    setColorState(newColor);
    applyTheme(mode, newColor);
    await saveSettings(mode, newColor);
  };

  const applyTheme = (mode: ThemeMode, color: ThemeColor) => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(mode);
    root.setAttribute("data-theme-color", color);
  };

  const saveSettings = async (mode: ThemeMode, color: ThemeColor) => {
    if (!user) return;

    const { data: existing } = await supabase
      .from("user_settings")
      .select("id")
      .eq("user_id", user.id)
      .single();

    if (existing) {
      await supabase
        .from("user_settings")
        .update({ theme_mode: mode, theme_color: color })
        .eq("user_id", user.id);
    } else {
      await supabase
        .from("user_settings")
        .insert({ user_id: user.id, theme_mode: mode, theme_color: color });
    }
  };

  return (
    <ThemeContext.Provider value={{ mode, color, setMode, setColor }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};
